import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext({});

// Backstop against missed Razorpay webhooks: never grant Pro access purely
// off the `plan` flag without also checking it hasn't already expired.
export function isProUser(profile) {
  if (profile?.plan !== 'paid') return false;
  if (!profile?.plan_expires_at) return true;
  return new Date(profile.plan_expires_at) > new Date();
}

// Last-known profile per user, used to render the correct plan instantly on
// load instead of defaulting to "free" while the network fetch is in flight
// (or stuck on free permanently if that fetch happens to fail).
const PROFILE_CACHE_KEY = 'indiads_profile_cache_v1';

function readCachedProfile(userId) {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROFILE_CACHE_KEY));
    return parsed?.id === userId ? parsed.data : null;
  } catch {
    return null;
  }
}

function writeCachedProfile(userId, data) {
  try {
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({ id: userId, data }));
  } catch {
    // localStorage unavailable (private browsing, quota, etc.) — non-fatal
  }
}

function clearCachedProfile() {
  try {
    localStorage.removeItem(PROFILE_CACHE_KEY);
  } catch {
    // ignore
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let initialized = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        const cached = readCachedProfile(session.user.id);
        if (cached) {
          // Render from last-known state immediately, then reconcile with a
          // fresh fetch in the background so a slow/failed network call
          // never strands the user on a stale or default "free" view.
          setProfile(cached);
          setLoading(false);
          fetchProfile(session.user.id, { background: true });
        } else {
          await fetchProfile(session.user.id);
        }
      } else {
        setProfile(null);
        clearCachedProfile();
        setLoading(false);
      }
      initialized = true;
    });

    // Fallback: if onAuthStateChange doesn't fire within 3s, stop loading
    const fallback = setTimeout(() => {
      if (!initialized) setLoading(false);
    }, 3000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(fallback);
    };
  }, []);

  // `background: true` is used when we've already rendered from a cached
  // profile — failures here just leave the cached value in place rather than
  // clearing `profile` or touching `loading`.
  async function fetchProfile(userId, { background = false } = {}) {
    const MAX_ATTEMPTS = 3;
    let data = null;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('timeout')), 3000)
        );
        const query = supabase.from('profiles').select('*').eq('id', userId).single();
        const result = await Promise.race([query, timeout]);
        if (!result.error && result.data) {
          data = result.data;
          break;
        }
      } catch (e) {
        // network error or timeout — retry below
      }
      if (attempt < MAX_ATTEMPTS) {
        await new Promise(r => setTimeout(r, attempt * 500));
      }
    }

    if (data) {
      // Enrich with Google avatar if available
      let googleAvatar = null;
      try {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        googleAvatar = currentUser?.user_metadata?.avatar_url || currentUser?.user_metadata?.picture || null;
      } catch (e) {
        // non-fatal — proceed without avatar enrichment
      }
      const enriched = { ...data, avatar_url: data.avatar_url || googleAvatar || null };
      setProfile(enriched);
      writeCachedProfile(userId, enriched);
    }
    // else: profile fetch failed after all retries — leave whatever is
    // already in state (cached profile in the background case, or null on
    // a first-ever visit where no cache exists yet).

    if (!background) setLoading(false);
  }

  async function refreshProfile() {
    if (user) await fetchProfile(user.id);
  }

  async function signUp(email, password, fullName, { skipOnboarding = false } = {}) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    });
    if (error) throw error;
    if (data.user) {
      await supabase.from('profiles').upsert({
        id: data.user.id,
        email,
        full_name: fullName,
        plan: 'free',
        onboarding_done: skipOnboarding,
      });
    }
    return data;
  }

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  }

  async function verifySignupOtp(email, token) {
    const { data, error } = await supabase.auth.verifyOtp({ email, token, type: 'signup' });
    if (error) throw error;
    return data;
  }

  async function resendSignupOtp(email) {
    const { error } = await supabase.auth.resend({ type: 'signup', email });
    if (error) throw error;
  }

  async function signInWithGoogleCredential(idToken) {
    const { data, error } = await supabase.auth.signInWithIdToken({ provider: 'google', token: idToken });
    if (error) throw error;
    let isNewUser = false;
    if (data.user) {
      // Google's one-tap flow doesn't distinguish "created an account" from
      // "logged into an existing one" — created_at and last_sign_in_at land
      // within milliseconds of each other only on a brand-new account.
      isNewUser = !!(
        data.user.created_at && data.user.last_sign_in_at &&
        Math.abs(new Date(data.user.last_sign_in_at) - new Date(data.user.created_at)) < 5000
      );
      await supabase.from('profiles').upsert({
        id: data.user.id,
        email: data.user.email,
        full_name: data.user.user_metadata?.full_name || data.user.user_metadata?.name || '',
        plan: 'free',
        onboarding_done: true,
      }, { onConflict: 'id', ignoreDuplicates: true });
    }
    return { ...data, isNewUser };
  }

  async function signOut() {
    await supabase.auth.signOut();
    clearCachedProfile();
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, signUp, signIn, signInWithGoogleCredential, signOut, refreshProfile, verifySignupOtp, resendSignupOtp, supabase }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
